import React, { useMemo, useState } from 'react';

import Logo from 'assets/images/logo-white.png';
import cn from 'classnames';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import MetaTags from 'react-meta-tags';
import { Link, withRouter } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import * as Yup from 'yup';

import { MCCard, MCField } from 'components/mc';

import recoveryService from '../../services/recoveryService';

import { t } from '../../i18n';
import s from './scss/SetPassword.module.scss';
import authStyles from './scss/AuthPages.module.scss';
import Footer from 'components/VerticalLayout/Footer';

// Final step of account recovery (Option B). The client follows a one-time link
// the operator issues after approval:
//   {RECOVERY_SETPW_URL}?token=...&case_ref=...
// Both params are read from the query string and forwarded to the service.
const readParam = (search, key) => {
  try {
    return new URLSearchParams(search || '').get(key) || '';
  } catch {
    return '';
  }
};

// Outcome buckets — drive which copy and CTA the success/error screen shows.
const RESULT_NONE = '';
const RESULT_OK = 'ok';
const RESULT_INVALID = 'invalid'; // 400 — generic, no enumeration
const RESULT_CONFLICT = 'conflict'; // 409 — email already in use
const RESULT_LOCKED = 'locked'; // 423 — too many attempts
const RESULT_TRANSIENT = 'transient'; // 503 / network — retry-safe

const CheckIcon = () => (
  <svg
    className={s.confirmIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
);

const EyeIcon = ({ off }) => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
    {off ? <path d="M4 4l16 16" /> : null}
  </svg>
);

EyeIcon.propTypes = { off: PropTypes.bool };

// Reveal-toggle password input styled on the wizard's dark canvas (no reactstrap,
// no emoji glyph — the show/hide control is an inline SVG).
const SecretInput = ({ id, name, placeholder, value, onChange, onBlur, invalid }) => {
  const [show, setShow] = useState(false);
  return (
    <div className={s.secretWrap}>
      <input
        id={id}
        name={name}
        type={show ? 'text' : 'password'}
        autoComplete="new-password"
        className={cn(s.input, s.secretInput, { [s.invalid]: invalid })}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      <button
        type="button"
        className={s.revealBtn}
        onClick={() => setShow((v) => !v)}
        aria-label={show ? t('common_password_hide') : t('common_password_show')}>
        <EyeIcon off={show} />
      </button>
    </div>
  );
};

SecretInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  invalid: PropTypes.bool,
};

const SetPassword = (props) => {
  const token = useMemo(() => readParam(props.location?.search, 'token'), [props.location?.search]);
  const caseRef = useMemo(
    () => readParam(props.location?.search, 'case_ref'),
    [props.location?.search],
  );

  const [result, setResult] = useState(RESULT_NONE);
  const [loading, setLoading] = useState(false);

  // Missing/garbled link — same generic copy as a server 400, no enumeration.
  const linkMissing = !token || !caseRef;

  const form = useFormik({
    enableReinitialize: false,
    initialValues: { password: '', confirmPassword: '' },
    // Mirror of the existing reset flow (RecoverPassword.jsx) validators.
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, t('common_password_hint_small'))
        .matches(
          /^(?=.*?[A-Z])(?=(.*[a-z]){1,})(?=(.*[\d]){1,})(?=(.*[\W]){1,})(?!.*\s).{8,}$/,
          t('common_password_hint_validation'),
        )
        .required(t('common_password_hint_required')),
      confirmPassword: Yup.string()
        .required(t('common_password_hint_repeat'))
        .oneOf([Yup.ref('password'), null], t('common_password_hint_not_match')),
    }),
    onSubmit: async (values) => {
      if (linkMissing) {
        setResult(RESULT_INVALID);
        return;
      }
      setLoading(true);
      try {
        await recoveryService.setPassword(caseRef, token, values.password);
        setResult(RESULT_OK);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 400) setResult(RESULT_INVALID);
        else if (status === 409) setResult(RESULT_CONFLICT);
        else if (status === 423) setResult(RESULT_LOCKED);
        else setResult(RESULT_TRANSIENT); // 503 or no response (network) — retry-safe
      } finally {
        setLoading(false);
      }
    },
  });

  const renderForm = () => (
    <form
      className={s.fields}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}>
      {linkMissing ? (
        <p className={cn(s.alert, s.alertError)}>{t('set_password_invalid')}</p>
      ) : null}

      {result === RESULT_INVALID ? (
        <p className={cn(s.alert, s.alertError)}>{t('set_password_invalid')}</p>
      ) : null}
      {result === RESULT_CONFLICT ? (
        <p className={cn(s.alert, s.alertError)}>{t('set_password_conflict')}</p>
      ) : null}
      {result === RESULT_LOCKED ? (
        <p className={cn(s.alert, s.alertError)}>{t('set_password_locked')}</p>
      ) : null}
      {result === RESULT_TRANSIENT ? (
        <p className={cn(s.alert, s.alertError)}>{t('set_password_transient')}</p>
      ) : null}

      <MCField
        label={t('common_password')}
        htmlFor="password"
        error={form.touched.password && form.errors.password ? form.errors.password : null}>
        <SecretInput
          id="password"
          name="password"
          placeholder={t('common_password_placeholder')}
          value={form.values.password}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          invalid={!!(form.touched.password && form.errors.password)}
        />
      </MCField>

      <MCField
        label={t('auth_recover_password_confirm_password')}
        htmlFor="confirmPassword"
        error={
          form.touched.confirmPassword && form.errors.confirmPassword
            ? form.errors.confirmPassword
            : null
        }>
        <SecretInput
          id="confirmPassword"
          name="confirmPassword"
          placeholder={t('common_password_placeholder')}
          value={form.values.confirmPassword}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          invalid={!!(form.touched.confirmPassword && form.errors.confirmPassword)}
        />
      </MCField>

      <div className={s.actions}>
        <button className={s.primaryBtn} type="submit" disabled={loading || linkMissing}>
          {loading ? (
            <>
              <Spinner className="spinner" /> {t('set_password_submit')}
            </>
          ) : (
            t('set_password_submit')
          )}
        </button>
      </div>
    </form>
  );

  const renderDone = () => (
    <div className={s.confirm}>
      <CheckIcon />
      <h5 className={s.confirmTitle}>{t('set_password_done_title')}</h5>
      <p className={s.note}>{t('set_password_done_text')}</p>
      <p className={cn(s.note, s.noteWarn)}>{t('set_password_done_freeze')}</p>
      <div className={s.actions} style={{ width: '100%' }}>
        <Link to="/login" className={s.primaryBtn}>
          {t('auth_register_login')}
        </Link>
      </div>
    </div>
  );

  const done = result === RESULT_OK;

  return (
    <React.Fragment>
      <MetaTags>
        <title>{t('set_password_meta_title')}</title>
      </MetaTags>
      <div className={cn(authStyles.wrapper, s.scope)}>
        <div className={authStyles.formWrapper}>
          <img src={Logo} alt="logo" className={authStyles.logo} />
          <div>
            <h4 className={authStyles.title}>{t('set_password_title')}</h4>
            <p className={authStyles.subtitle}>
              {done ? t('set_password_done_subtitle') : t('set_password_subtitle')}
            </p>
          </div>

          <MCCard pad="lg" className={s.card}>
            {done ? renderDone() : renderForm()}
          </MCCard>
        </div>
        <Footer authFooter />
      </div>
    </React.Fragment>
  );
};

SetPassword.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
};

export default withRouter(SetPassword);
